import values from 'lodash/values';
import { orderBy } from 'natural-orderby';
import { getUniqueArray } from './dataFormatUtils';

export function getGaugeDictionary(
    firstGroupKey,
    firstGroupLabels,
    secondGroupLabels
) {
    const defaultSecondGroupValues = secondGroupLabels.reduce(
        (result, label) => ({
            ...result,
            [label]: 0,
        }),
        {}
    );

    return firstGroupLabels.reduce(
        (results, label) => ({
            ...results,
            [label]: {
                [firstGroupKey]: label,
                ...defaultSecondGroupValues,
            },
        }),
        {}
    );
}

function insertDataIntoGaugeDictionary(
    metricSeries,
    firstGroupBy,
    secondGroupBy,
    metric,
    secondGroupByLabels,
    datasetKeyedByGroup,
    numberDecimalFormatter
) {
    const gaugeGroupDictionary = { ...datasetKeyedByGroup };

    metricSeries.forEach((dataPoint) => {
        const dataPointGroupKey = dataPoint[firstGroupBy];

        secondGroupByLabels.forEach((label) => {
            const originalLabel = `${label}`.split(' (')[0];
            // eslint-disable-next-line eqeqeq
            if (dataPoint[secondGroupBy] == originalLabel) {
                gaugeGroupDictionary[dataPointGroupKey] = {
                    ...gaugeGroupDictionary[dataPointGroupKey],
                    [label]: dataPoint[metric]
                        ? numberDecimalFormatter(dataPoint[metric])
                        : 0,
                };
            }
        });
    });

    return gaugeGroupDictionary;
}

export function formatStackedGaugeDataset(
    metricSeries,
    firstGroupBy,
    secondGroupBy,
    metric,
    firstGroupLabels,
    secondGroupLabels,
    numberDecimalFormatter
) {
    const datasetKeyedByGroup = getGaugeDictionary(
        firstGroupBy,
        firstGroupLabels,
        secondGroupLabels
    );

    const withSourceData = insertDataIntoGaugeDictionary(
        metricSeries,
        firstGroupBy,
        secondGroupBy,
        metric,
        secondGroupLabels,
        datasetKeyedByGroup,
        numberDecimalFormatter
    );

    return values(withSourceData);
}

export function formatGaugeDataset(
    metricSeries,
    groupBy,
    metric,
    numberDecimalFormatter,
    sortByTop
) {
    const originalMetricName = metric.split(' (')[0];

    const formattedData = metricSeries.map((series) => ({
        [groupBy]: series[groupBy],
        [metric]: series[originalMetricName]
            ? numberDecimalFormatter(series[originalMetricName])
            : 0,
    }));

    return sortByTop
        ? orderBy(formattedData, [metric], ['desc'])
        : orderBy(formattedData, [groupBy]);
}

function getSourceDataForGaugeMetrics(
    metricSeries,
    groupBy,
    metricNames,
    datasetKeyedByGroup,
    numberDecimalFormatter
) {
    const gaugeGroupDictionary = { ...datasetKeyedByGroup };

    metricSeries.forEach((dataPoint) => {
        metricNames.forEach((metricName) => {
            const dataPointGroupKey = dataPoint[groupBy];
            const originalMetricName = metricName.split(' (')[0];

            gaugeGroupDictionary[dataPointGroupKey] = {
                ...gaugeGroupDictionary[dataPointGroupKey],
                [metricName]: dataPoint[originalMetricName]
                    ? numberDecimalFormatter(dataPoint[originalMetricName])
                    : 0,
            };
        });
    });

    return gaugeGroupDictionary;
}

export function formatGaugeDatasetMultiMetric(
    metricSeries,
    groupBy,
    groupLabels,
    metrics,
    numberDecimalFormatter
) {
    const datasetKeyedByGroup = getGaugeDictionary(
        groupBy,
        groupLabels,
        metrics
    );

    const withSourceData = getSourceDataForGaugeMetrics(
        metricSeries,
        groupBy,
        metrics,
        datasetKeyedByGroup,
        numberDecimalFormatter
    );

    return values(withSourceData);
}

export function getFieldsForMultiMetricRequest(
    metricRequest,
    metricSource,
    numberDecimalFormatter,
    suffix
) {
    const groupBy = metricRequest.groupBy ? metricRequest.groupBy[0] : 'Value';

    const groupByLabels = orderBy(
        getUniqueArray(metricSource.metricSeries.map((x) => x[groupBy]))
    );

    const dataLabels = suffix
        ? metricRequest.metrics.map((x) => `${x} (${suffix})`)
        : metricRequest.metrics;

    const formattedData = formatGaugeDatasetMultiMetric(
        metricSource.metricSeries,
        groupBy,
        groupByLabels,
        dataLabels,
        numberDecimalFormatter
    );

    return { dataLabels, groupBy, formattedData };
}

export function getFieldsForSingleGroupAndMetricRequest(
    metricRequest,
    metricSource,
    numberDecimalFormatter,
    suffix
) {
    const groupBy = metricRequest.groupBy[0];
    const metric = suffix
        ? `${metricRequest.metrics[0]} (${suffix})`
        : metricRequest.metrics[0];
    const dataLabels = [metric];
    const sortByTop = metricRequest.recordLimit;

    const formattedData = formatGaugeDataset(
        metricSource.metricSeries,
        groupBy,
        metric,
        numberDecimalFormatter,
        sortByTop
    );

    return { dataLabels, groupBy, formattedData };
}

export function getFieldsForMultiGroupAndMetricRequest(
    metricRequest,
    metricSource,
    numberDecimalFormatter,
    suffix
) {
    const metric = metricRequest.metrics[0];
    const groupBy = metricRequest.groupBy[0];
    const secondGroupBy = metricRequest.groupBy[1];

    const firstGroupLabels = orderBy(
        getUniqueArray(metricSource.metricSeries.map((x) => x[groupBy]))
    );

    // prettier-ignore
    const dataLabels = orderBy(
        getUniqueArray(
            suffix
                ? metricSource.metricSeries.map(
                    x => `${x[secondGroupBy]} (${suffix})`
                )
                : metricSource.metricSeries.map(x => x[secondGroupBy])
        )
    );

    const formattedData = formatStackedGaugeDataset(
        metricSource.metricSeries,
        groupBy,
        secondGroupBy,
        metric,
        firstGroupLabels,
        dataLabels,
        numberDecimalFormatter
    );

    return { dataLabels, groupBy, formattedData };
}

export function getGaugeFields(
    metricRequest,
    metricSource,
    numberDecimalFormatter,
    suffix = ''
) {
    if (metricRequest.metrics.length > 1) {
        return getFieldsForMultiMetricRequest(
            metricRequest,
            metricSource,
            numberDecimalFormatter,
            suffix
        );
    }

    if (metricRequest.groupBy.length === 1) {
        return getFieldsForSingleGroupAndMetricRequest(
            metricRequest,
            metricSource,
            numberDecimalFormatter,
            suffix
        );
    }

    return getFieldsForMultiGroupAndMetricRequest(
        metricRequest,
        metricSource,
        numberDecimalFormatter,
        suffix
    );
}

export function getFieldsForMultiMetricPie(
    metricRequest,
    metricSource,
    numberDecimalFormatter
) {
    const series = metricSource.metricSeries[0] || {};

    const formattedData = metricRequest.metrics.map((metricName) => ({
        metric: metricName,
        value: series[metricName]
            ? numberDecimalFormatter(series[metricName])
            : 0,
    }));

    return {
        nameKey: 'metric',
        valueKey: 'value',
        formattedData,
    };
}

export function getFieldsForSingleGroupPie(
    metricRequest,
    metricSource,
    numberDecimalFormatter
) {
    const metric = metricRequest.metrics[0];
    const groupBy = metricRequest.groupBy[0];

    const formattedData = metricSource.metricSeries.map((series) => ({
        [groupBy]: series[groupBy],
        [metric]: series[metric] ? numberDecimalFormatter(series[metric]) : 0,
    }));

    return {
        nameKey: groupBy,
        valueKey: metric,
        formattedData: orderBy(formattedData, [groupBy]),
    };
}

export function getGaugePieFields(
    metricRequest,
    metricSource,
    numberDecimalFormatter
) {
    if (metricRequest.metrics.length > 1) {
        return getFieldsForMultiMetricPie(
            metricRequest,
            metricSource,
            numberDecimalFormatter
        );
    }

    if (metricRequest.groupBy.length === 1) {
        return getFieldsForSingleGroupPie(
            metricRequest,
            metricSource,
            numberDecimalFormatter
        );
    }

    return null;
}
